/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AppComponent } from './app.component';
import { HomeComponent } from './components/home.component';
import { FetchDataComponent } from './components/fetch-data.component';
import { BasicModalComponent } from './components/modals/basic-modal.component';
import { LoginModalComponent } from './components/modals/login-modal.component';
import { RemainingCreditsModalComponent } from './components/modals/remaining-credits-modal.component';
import { CreditUsageModalComponent } from './components/modals/credit-usage-modal.component';
import { WorkflowModalComponent } from './components/modals/workflow-modal.component';
import { SaveAsNewModalComponent } from './components/modals/save-as-new-modal.component';
import { JobsModalComponent } from './components/modals/jobs-modal.component';
import { XdaStringsService } from './services/xda-strings.service';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { WorkflowsComponent } from './components/workflows.component';
import { NavBarComponent } from './components/navigation/nav-bar.component';
import { NavAccountComponent } from './components/navigation/nav-account.component';
import { NavFooterComponent } from './components/navigation/nav-footer.component';
import { ForgotPasswordComponent } from './components/accounts/forgot-password.component';
import { ResetPasswordComponent } from './components/accounts/reset-password.component';
import { BasicAlertComponent } from './components/alerts/basic-alert.component';
import { PreventHrefNavDirective } from './directives/prevent-href-nav.directive';
import { AuthInterceptor } from './services/interceptors/auth-interceptor.service';
import { MyAccountComponent } from './components/accounts/my-account.component';
import { ChangePasswordComponent } from './components/accounts/change-password.component';
import { TosUpdateModalComponent } from './components/modals/tos-update-modal.component';
import { AddCustomStringModalComponent } from './components/redaction/modals/add-custom-string.component';
import { SelectCountryModalComponent } from './components/redaction/modals/select-country.component';
import { StandardRedactionFieldsModalComponent } from './components/redaction/modals/standard-redaction-fields-modal.component';
import { XrxCircularProgressComponent } from './components/controls/xrx-circular-progess-bar.component';
import { XrxToggleSwitchComponent } from './components/controls/xrx-toggle-switch.component';
import { XrxToggleSwitchGroupComponent } from './components/controls/xrx-toggle-switch-group.component';
import { XrxIncrementerComponent } from './components/controls/xrx-incrementer.component';
import { XrxMessageComponent } from './components/controls/xrx-message.component';
import { XrxSettingComponent } from './components/controls/xrx-setting.component';
import { XrxBannerComponent } from './components/controls/xrx-banner.component';
import { AccessListComponent } from './components/accessList/access-list.component';
import { StopEventPropagation } from './directives/stop-event-propagation.directive';
import { KeepPlaceholderDirective } from './directives/keep-placeholder.directive';
import { TableSort } from './directives/table-sort.directive';
import { Filter } from './pipes/filter.pipe';
import { XdaTranslate } from './pipes/xda-translate.pipe';
import { TableSortPipe } from './pipes/table-sort.pipe';
import { TableFilterPipe } from './pipes/table-filter.pipe';
import { SortColumnDirective } from './directives/sort-column.directive';
import { InviteUserComponent } from './components/accessList/invite-user.component';
import { BulkInvitaionComponent } from './components/accessList/invite-multip-users.modal.component';
import { EditRoleComponent } from './components/accessList/edit-role.component';
import { XrxSelectComponent } from './components/controls/xrx-select.component';
import { XrxMultiSelectComponent } from './components/controls/xrx-multi-select.component';
import { OrderBy } from './pipes/order-by.pipe';
import { XrxRadioComponent } from './components/controls/xrx-radio.component';
import { XrxInputComponent } from './components/controls/xrx-input.component';
import { XrxFileInputComponent } from './components/controls/xrx-file-input.component';
import { XrxOutputFileInputComponent } from './components/controls/xrx-output-file-input.component';
import { AuthGuardService as AuthGuard } from './services/auth-guard.service';
import { ManageSubscriptionComponent } from './components/manage-subscription.component';
import { MatchButtonWidthDirective } from './directives/match-button-width.directive';
import { HeaderExcludeAlertComponent } from './components/alerts/header-exclude-alert.component';
import { InputTabDirective } from './directives/input-tab.directive';
import { LongTouchDirective } from './directives/long-touch.directive';
import { StatusTrayComponent } from './components/status-tray.component';
import { XrxPopoverComponent } from './components/controls/xrx-popover.component';
import { XrxPrintSettingsComponent } from './components/controls/xrx-print-settings.component';
import { PrintOptionsModalComponent } from './components/modals/print-options-modal.component';
import { CookieConsentComponent } from './components/cookie-consent.component';
import { XrxIconPickerComponent } from './components/controls/xrx-icon-picker.component';
import { XrxSelectConversionComponent } from './components/controls/xrx-select-conversion.component';
import { XrxMultiTranslationComponent } from './components/controls/xrx-multi-translation.component';
import { XrxPopoverConversionComponent } from './components/controls/xrx-popover-conversion.component';
import { XrxRedactComponent } from './components/redaction/xrx-redact.component';
import { XrxDrmComponent } from './components/drm/xrx-drm.component';
import { XrxSummarizationComponent } from './components/summarization/xrx-summarization.component';
import { SummarizationMethodHelpComponent } from './components/summarization/modals/summarization-method-help.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DisplayResourceComponent } from './components/display-resource.component';
import { MiddlewareModule } from './middleware/middleware.module';
import { XaccModule } from './xacc/xacc.module';
import { SqdCommonModule } from './common/sqd-common.module';
import { MiddlewareReentryComponent } from './middleware/middleware-reentry.component';
import { XaccReentryComponent } from './xacc/xacc-reentry.component';
import { XpseEntryComponent } from './components/xpse-entry.component';
import { GuestJobsModalComponent } from './components/modals/guest-jobs-modal.component';
import { ActivityIndicatorModalComponent } from './components/modals/activity-indicator-modal.component';
import { ConfigureServicesFeaturesComponent } from './components/configureServicesFeatures/configure-services-features.component';
import { XrxMultiFileSelectComponent } from './components/controls/xrx-multi-file-select.component';
import { ActiveDocumentsComponent } from './components/activeDocuments/active-documents.component';
import { ActiveDocumentsModalComponent } from './components/activeDocuments/active-documents-modal.component';
import { DomainsModalComponent } from './components/activeDocuments/domains-modal.component';
import { XrxCalendarComponent } from './components/controls/xrx-calendar.component';
import { EditorModalComponent } from './components/modals/editor-modal.component';
import { XrxTextareaComponent } from './components/controls/xrx-textarea.component';
import { SecurityLevelHelpComponent } from './components/drm/modals/security-level-help.component';
import { EditAddressComponent } from './components/modals/edit-address.component';
import { XrxPaginationComponent } from './components/controls/xrx-pagination.component';
import { XrxPdfConvertComponent } from './components/pdfConvert/xrx-pdf-convert.component';
import { XrxXmFaxComponent } from './components/xmfax/xrx-xmfax.component';
import { CountryCodeModalComponent } from './components/xmfax/country-code-modal.component';
import { XrxInputGroupComponent } from './components/controls/xrx-input-group.component';
import { XrxJobHistoryModalComponent } from './components/automatedWorkflows/xrx-job-history-modal.component';
import { AutomatedWorkflowModalComponent } from './components/modals/automated-workflow-modal.component';
import { XrxTimeComponent } from './components/controls/xrx-time.component';
import { XrxMasterFileSelectComponent } from './components/controls/xrx-master-file-select.component';
import { SpinAlertComponent } from './components/alerts/spin-alert.component';
import { XrxSliderComponent } from './components/controls/xrx-slider.component';
import { XrxAudioConvertComponent } from './components/audioConvert/xrx-audio-convert.component';

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    DisplayResourceComponent,
    FetchDataComponent,
    BasicModalComponent,
    LoginModalComponent,
    RemainingCreditsModalComponent,
    CreditUsageModalComponent,
    WorkflowModalComponent,
    SaveAsNewModalComponent,
    JobsModalComponent,
    GuestJobsModalComponent,
    TosUpdateModalComponent,
    ActivityIndicatorModalComponent,
    WorkflowsComponent,
    StatusTrayComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    PreventHrefNavDirective,
    NavBarComponent,
    NavAccountComponent,
    NavFooterComponent,
    BasicAlertComponent,
    MyAccountComponent,
    ChangePasswordComponent,
    XrxInputComponent,
    XrxFileInputComponent,
    XrxOutputFileInputComponent,
    XrxCircularProgressComponent,
    XrxToggleSwitchComponent,
    XrxToggleSwitchGroupComponent,
    XrxIncrementerComponent,
    XrxMessageComponent,
    XrxSettingComponent,
    XrxBannerComponent,
    AccessListComponent,
    StopEventPropagation,
    KeepPlaceholderDirective,
    TableSort,
    Filter,
    XdaTranslate,
    TableSortPipe,
    TableFilterPipe,
    SortColumnDirective,
    InviteUserComponent,
    BulkInvitaionComponent,
    EditRoleComponent,
    XrxSelectComponent,
    XrxSelectConversionComponent,
    XrxMultiTranslationComponent,
    XrxPopoverConversionComponent,
    XrxRedactComponent,
    XrxMultiSelectComponent,
    XrxRadioComponent,
    OrderBy,
    MatchButtonWidthDirective,
    ManageSubscriptionComponent,
    HeaderExcludeAlertComponent,
    InputTabDirective,
    LongTouchDirective,
    XrxPopoverComponent,
    XrxPrintSettingsComponent,
    PrintOptionsModalComponent,
    CookieConsentComponent,
    XrxIconPickerComponent,
    AddCustomStringModalComponent,
    SelectCountryModalComponent,
    StandardRedactionFieldsModalComponent,
    XpseEntryComponent,
    ConfigureServicesFeaturesComponent,
    XrxMultiFileSelectComponent,
    ActiveDocumentsComponent,
    ActiveDocumentsModalComponent,
    DomainsModalComponent,
    XrxCalendarComponent,
    XrxMasterFileSelectComponent,
    EditorModalComponent,
    XrxPaginationComponent,
    EditorModalComponent,
    XrxTextareaComponent,
    XrxDrmComponent,
    SecurityLevelHelpComponent,
    XrxSummarizationComponent,
    SummarizationMethodHelpComponent,
    EditAddressComponent,
    XrxPdfConvertComponent,
    XrxXmFaxComponent,
    CountryCodeModalComponent,
    XrxInputGroupComponent,
    XrxJobHistoryModalComponent,
    AutomatedWorkflowModalComponent,
    XrxTimeComponent,
    SpinAlertComponent,
    XrxSliderComponent,
    XrxAudioConvertComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    NgbModule,
    FormsModule,
    MiddlewareModule,
    XaccModule,
    SqdCommonModule,
    RouterModule.forRoot(
      [
        {
          path: '',
          component: HomeComponent,
          pathMatch: 'full',
          canActivate: [AuthGuard],
        },
        {
          path: 'workflows',
          component: WorkflowsComponent,
          canActivate: [AuthGuard],
        },
        {
          path: 'active-documents',
          component: ActiveDocumentsComponent,
          canActivate: [AuthGuard],
        },
        //{ path: 'categories', component: CategoriesComponent, canActivate: [AuthGuard] },
        {
          path: 'access-list',
          component: AccessListComponent,
          canActivate: [AuthGuard],
        },
        {
          path: 'manage-subscription',
          component: ManageSubscriptionComponent,
          canActivate: [AuthGuard],
        },
        {
          path: 'middleware-reentry/:connector',
          component: MiddlewareReentryComponent,
          pathMatch: 'full',
        },
        {
          path: 'middleware-reentry/:connector/:region',
          component: MiddlewareReentryComponent,
          pathMatch: 'full',
        },
        {
          path: 'xacc-reentry/:connector',
          component: XaccReentryComponent,
          pathMatch: 'full',
        },
        {
          path: 'xacc-reentry/:connector/:region',
          component: XaccReentryComponent,
          pathMatch: 'full',
        },
        { path: 'terms', component: DisplayResourceComponent },
        { path: 'plans-pricing', component: FetchDataComponent },
        { path: 'password-reset', component: HomeComponent, pathMatch: 'full' },
        {
          path: 'refreshToken/:token/:action',
          component: HomeComponent,
          pathMatch: 'full',
        },
        { path: 'refresh/:action', component: HomeComponent, pathMatch: 'full' },
        { path: 'refresh', component: HomeComponent, pathMatch: 'full' },
        {
          path: 'xpse/:file/:storage',
          component: XpseEntryComponent,
          pathMatch: 'full',
        },
        { path: '**', redirectTo: '/' },
      ],
      {},
    ),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [XdaStringsService],
      useFactory: (xdaStringsService: XdaStringsService) => {
        return () => {
          return xdaStringsService.loadStrings();
        };
      },
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
